// src/components/Social/CommunityProfileEditor.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';
import './CommunityProfileEditor.css';

const API_URL = API_BASE_URL;

const HOST = (() => {
  try {
    const u = new URL(API_BASE_URL);
    return `${u.protocol}//${u.host}`;
  } catch {
    // fallback: strip trailing /api or /api/ from the string
    return API_BASE_URL.replace(/\/api\/?$/, '');
  }
})();

const resolvePhotoUrl = (raw, role = 'athlete') => {
  if (!raw) return '';
  const v = String(raw).trim();
  if (!v) return '';
  // data URI (base64) — devolver tal cual
  if (/^data:image\//i.test(v)) return v;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith('/')) return `${HOST}${v}`;
  // solo filename — construir ruta según rol
  if (role === 'trainer') return `${HOST}/uploads/trainers/${v}`;
  return `${HOST}/uploads/athletes/${v}`;
};

const BIO_MAX_LENGTH = 220;

const SPORT_OPTIONS = [
  { key: 'running', icon: '🏃' },
  { key: 'cycling', icon: '🚴' },
  { key: 'trail', icon: '⛰️' },
  { key: 'swimming', icon: '🏊' },
  { key: 'strength', icon: '💪' },
  { key: 'triathlon', icon: '🏅' },
];

const EMPTY_PROFILE = {
  isEnabled: false,
  displayName: '',
  city: '',
  country: '',
  bio: '',
  sports: [],
  preferredSports: [],
  // NUEVO: preferencia de género para el matching
  preferredGender: 'any', // 'any' | 'women' | 'men'
  showAge: false,
  showGender: false,
  showCity: true,
};

const CommunityProfileEditor = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  // 🔙 ahora SIEMPRE vuelve a la Comunidad (/social)
  const handleBackToDashboard = () => {
    navigate('/social');
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError('');
        setSaved(false);

        if (!userId) {
          setError(t('social.profile.errors.userIdNotFound'));
          setLoading(false);
          return;
        }

        const res = await fetch(
          `${API_URL}/api/community/profile/me?userId=${userId}`,
          { credentials: 'include' }
        );

        if (!res.ok) {
          throw new Error(t('social.profile.errors.loadProfile'));
        }

        const data = await res.json();

        // Si el perfil de comunidad no tiene foto, la buscamos en el perfil del atleta o entrenador
        let photoUrl = data.photoUrl || '';
        if (!photoUrl) {
          try {
            const role = localStorage.getItem('role') || data.role || 'athlete';
            const profileEndpoint = role === 'trainer'
              ? `${API_URL}/api/trainers/profile/${userId}`
              : `${API_URL}/api/athletes/profile/${userId}`;

            const profileRes = await fetch(profileEndpoint, { credentials: 'include' });
            if (profileRes.ok) {
              const profileData = await profileRes.json();
              const p = profileData.trainer || profileData.athlete || profileData;
              const rawPhoto =
                p.profilePhoto ||
                p.photoUrl ||
                p.avatarUrl ||
                p.photo ||
                '';
              photoUrl = resolvePhotoUrl(rawPhoto, role);
            }
          } catch (_) {}
        }

        setProfile({
          ...EMPTY_PROFILE,
          ...data,
          photoUrl,
          preferredGender: data.preferredGender || 'any',
        });
      } catch (err) {
        console.error('[CommunityProfileEditor] load error', err);
        const msg =
          err.message === 'Failed to fetch'
            ? t('social.profile.errors.serverConnectionLoad')
            : err.message || t('social.profile.errors.loadGeneric');
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, t]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const isSportSelected = (label) =>
    (profile.sports || []).some(
      (s) => String(s).toLowerCase() === String(label).toLowerCase()
    );

  const toggleSport = (label) => {
    setProfile((prev) => {
      const current = prev.sports || [];
      const exists = current.some(
        (s) => String(s).toLowerCase() === String(label).toLowerCase()
      );
      return {
        ...prev,
        sports: exists
          ? current.filter((s) => String(s).toLowerCase() !== String(label).toLowerCase())
          : [...current, label],
      };
    });
  };

  const removeSport = (label) => {
    setProfile((prev) => ({
      ...prev,
      sports: (prev.sports || []).filter(
        (s) => String(s).toLowerCase() !== String(label).toLowerCase()
      ),
    }));
  };

  const [customSport, setCustomSport] = useState('');

  const addCustomSport = () => {
    const clean = customSport.trim();
    if (!clean) return;
    if (!isSportSelected(clean)) {
      setProfile((prev) => ({ ...prev, sports: [...(prev.sports || []), clean] }));
    }
    setCustomSport('');
  };

  // Chips ya elegidos que no están en la lista predefinida (deportes libres)
  const customSelectedSports = (profile.sports || []).filter(
    (s) => !SPORT_OPTIONS.some((opt) => t(`social.profile.sportsOptions.${opt.key}`).toLowerCase() === String(s).toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      setError(t('social.profile.errors.userIdMissingSave'));
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSaved(false);

      const res = await fetch(
        `${API_URL}/api/community/profile/me?userId=${userId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(profile),
        }
      );

      if (!res.ok) {
        throw new Error(t('social.profile.errors.saveProfile'));
      }

      const data = await res.json();
      setProfile((prev) => ({
        ...prev,
        ...data,
        preferredGender: data.preferredGender || prev.preferredGender || 'any',
      }));
      setSaved(true);
    } catch (err) {
      console.error('[CommunityProfileEditor] save error', err);
      const msg =
        err.message === 'Failed to fetch'
          ? t('social.profile.errors.serverConnectionSave')
          : err.message || t('social.profile.errors.saveGeneric');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="social-profile-wrapper">
        <div className="social-profile-card">
          <div className="social-profile-loading">{t('social.profile.loading')}</div>
        </div>
      </div>
    );
  }

  const previewInitials =
    (profile.displayName || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('') || '?';

  // Foto de perfil: igual que SocialHub — primero photos[], luego photoUrl
  const previewPhoto = (() => {
    const role = profile.role || localStorage.getItem('role') || 'athlete';
    if (Array.isArray(profile.photos) && profile.photos.length > 0) {
      const primary =
        profile.photos.find((p) => p?.isPrimary)?.url ||
        profile.photos[0]?.url ||
        profile.photos[0];
      return resolvePhotoUrl(primary, role);
    }
    return resolvePhotoUrl(profile.photoUrl, role);
  })();

  const previewRoleLabel =
    profile.role === 'trainer' ? t('common.trainer') : t('common.athlete');

  const previewLocation = [profile.showCity ? profile.city : '', profile.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="social-profile-wrapper">
      <div className="social-profile-header d-flex justify-content-between align-items-center">
        <div>
          <h2>{t('social.myCommunityProfile')}</h2>
          <p>
            {t('social.profile.subtitle')}
          </p>
        </div>

        {/* 🔙 Botón volver a la comunidad */}
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={handleBackToDashboard}
        >
          {t('social.backToCommunity')}
        </button>
      </div>

      {error && (
        <div className="social-profile-alert social-profile-alert--error">
          {error}
        </div>
      )}
      {saved && !error && (
        <div className="social-profile-alert social-profile-alert--ok">
          {t('social.profile.savedSuccess')}
        </div>
      )}

      <div className="social-profile-layout">
        <div className="social-profile-card social-profile-main">
          <form className="social-profile-form" onSubmit={handleSubmit}>
            <div className="social-profile-toggle-row social-profile-toggle-row--enable">
              <div className="social-profile-toggle-row-text">
                <strong>{t('social.profile.enableProfile')}</strong>
              </div>
              <label className="social-toggle">
                <input
                  type="checkbox"
                  className="social-toggle-input"
                  checked={!!profile.isEnabled}
                  onChange={handleChange('isEnabled')}
                />
                <span className="social-toggle-track">
                  <span className="social-toggle-thumb" />
                </span>
              </label>
            </div>

            <div className="social-profile-block">
              <h3 className="social-profile-block-title">
                <span aria-hidden="true">🧑</span> {t('social.profile.basicInfo')}
              </h3>

              <label className="social-profile-field">
                <span>{t('social.profile.displayName')}</span>
                <input
                  type="text"
                  value={profile.displayName || ''}
                  onChange={handleChange('displayName')}
                  placeholder={t('social.profile.displayNamePlaceholder')}
                />
              </label>

              <div className="social-profile-inline">
                <label className="social-profile-field">
                  <span>{t('social.profile.city')}</span>
                  <input
                    type="text"
                    value={profile.city || ''}
                    onChange={handleChange('city')}
                    placeholder={t('social.profile.cityPlaceholder')}
                  />
                </label>

                <label className="social-profile-field">
                  <span>{t('social.profile.country')}</span>
                  <input
                    type="text"
                    value={profile.country || ''}
                    onChange={handleChange('country')}
                    placeholder={t('social.profile.countryPlaceholder')}
                  />
                </label>
              </div>

              <label className="social-profile-field">
                <span>{t('social.profile.bio')}</span>
                <textarea
                  rows={3}
                  maxLength={BIO_MAX_LENGTH}
                  value={profile.bio || ''}
                  onChange={handleChange('bio')}
                  placeholder={t('social.profile.bioPlaceholder')}
                />
                <span className="social-profile-char-counter">
                  {(profile.bio || '').length}/{BIO_MAX_LENGTH}
                </span>
              </label>
            </div>

            <div className="social-profile-block">
              <h3 className="social-profile-block-title">
                <span aria-hidden="true">🏃</span> {t('social.profile.sportsSection')}
              </h3>
              <p className="social-profile-hint">{t('social.profile.sportsHint')}</p>

              <div className="social-profile-chip-row">
                {SPORT_OPTIONS.map((opt) => {
                  const label = t(`social.profile.sportsOptions.${opt.key}`);
                  const selected = isSportSelected(label);
                  return (
                    <button
                      type="button"
                      key={opt.key}
                      className={`social-profile-chip${selected ? ' social-profile-chip--selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => toggleSport(label)}
                    >
                      <span aria-hidden="true">{opt.icon}</span> {label}
                    </button>
                  );
                })}

                {customSelectedSports.map((s) => (
                  <span
                    key={s}
                    className="social-profile-chip social-profile-chip--selected social-profile-chip--custom"
                  >
                    {s}
                    <button
                      type="button"
                      className="social-profile-chip-remove"
                      aria-label={t('social.profile.removeSport', { sport: s })}
                      onClick={() => removeSport(s)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <div className="social-profile-custom-sport-row">
                <input
                  type="text"
                  value={customSport}
                  onChange={(e) => setCustomSport(e.target.value)}
                  placeholder={t('social.profile.addCustomSportPlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomSport();
                    }
                  }}
                />
                <button
                  type="button"
                  className="social-meetups-btn"
                  onClick={addCustomSport}
                >
                  {t('social.profile.addCustomSport')}
                </button>
              </div>
            </div>

            {/* NUEVO bloque de preferencias de partner */}
            <div className="social-profile-block">
              <h3 className="social-profile-block-title">
                <span aria-hidden="true">👥</span> {t('social.profile.partnerPreferences')}
              </h3>
              <p className="social-profile-hint">
                {t('social.profile.partnerPreferencesHint')}
              </p>

              <div
                className="social-profile-segmented"
                role="radiogroup"
                aria-label={t('social.profile.partnerPreferences')}
              >
                {['any', 'women', 'men'].map((val) => (
                  <button
                    type="button"
                    key={val}
                    role="radio"
                    aria-checked={profile.preferredGender === val}
                    className={`social-profile-segment${
                      profile.preferredGender === val ? ' social-profile-segment--active' : ''
                    }`}
                    onClick={() =>
                      setProfile((prev) => ({ ...prev, preferredGender: val }))
                    }
                  >
                    {t(`social.profile.preferredGender.${val}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="social-profile-block">
              <h3 className="social-profile-block-title">
                <span aria-hidden="true">🔒</span> {t('social.profile.privacy')}
              </h3>

              <div className="social-profile-toggle-row">
                <div className="social-profile-toggle-row-text">
                  {t('social.profile.showAge')}
                </div>
                <label className="social-toggle">
                  <input
                    type="checkbox"
                    className="social-toggle-input"
                    checked={!!profile.showAge}
                    onChange={handleChange('showAge')}
                  />
                  <span className="social-toggle-track">
                    <span className="social-toggle-thumb" />
                  </span>
                </label>
              </div>

              <div className="social-profile-toggle-row">
                <div className="social-profile-toggle-row-text">
                  {t('social.profile.showGender')}
                </div>
                <label className="social-toggle">
                  <input
                    type="checkbox"
                    className="social-toggle-input"
                    checked={!!profile.showGender}
                    onChange={handleChange('showGender')}
                  />
                  <span className="social-toggle-track">
                    <span className="social-toggle-thumb" />
                  </span>
                </label>
              </div>

              <div className="social-profile-toggle-row">
                <div className="social-profile-toggle-row-text">
                  {t('social.profile.showCity')}
                </div>
                <label className="social-toggle">
                  <input
                    type="checkbox"
                    className="social-toggle-input"
                    checked={!!profile.showCity}
                    onChange={handleChange('showCity')}
                  />
                  <span className="social-toggle-track">
                    <span className="social-toggle-thumb" />
                  </span>
                </label>
              </div>
            </div>

            <div className="social-profile-actions">
              <button
                type="submit"
                className="social-cta-primary"
                disabled={saving}
              >
                {saving ? t('social.profile.saving') : t('social.profile.saveProfile')}
              </button>
            </div>
          </form>
        </div>

        <aside className="social-profile-preview">
          <div
            className={`social-profile-preview-card${
              !profile.isEnabled ? ' social-profile-preview-card--disabled' : ''
            }`}
          >
            <div className="social-profile-preview-photo">
              {previewPhoto ? (
                <img
                  src={previewPhoto}
                  alt={profile.displayName || ''}
                  className="social-profile-preview-img"
                />
              ) : (
                <span className="social-profile-preview-initials">{previewInitials}</span>
              )}
            </div>
            <div className="social-profile-preview-gradient" />
            <div className="social-profile-preview-info">
              <div className="social-profile-preview-name-row">
                <span className="social-profile-preview-name">
                  {profile.displayName || t('social.myProfile')}
                </span>
                <span className="social-profile-preview-badge">{previewRoleLabel}</span>
              </div>

              {(profile.sports || []).length > 0 && (
                <div className="social-profile-preview-sports">
                  {(profile.sports || []).join(' · ')}
                </div>
              )}

              {previewLocation && (
                <div className="social-profile-preview-location">
                  <span aria-hidden="true">📍</span> {previewLocation}
                </div>
              )}

              {profile.bio && (
                <div className="social-profile-preview-bio">{profile.bio}</div>
              )}
            </div>
          </div>

          {!profile.isEnabled && (
            <div className="social-profile-preview-disabled-hint">
              {t('social.profile.previewDisabledHint')}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CommunityProfileEditor;
